import { BookOpen, Code2, Info, Mail, type LucideIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

type Item = {
    label: string;
    icon: LucideIcon;
    href: string;
    preview: string;
};

const resources: Item[] = [
    {
        label: "About",
        icon: Info,
        href: "/about",
        preview: "https://cdn.pixro.ai/f21d7f71-7710-40f2-9b33-d498cfc1d123.jpg",
    },
    {
        label: "Blogs",
        icon: BookOpen,
        href: "/blogs",
        preview: "https://cdn.pixro.ai/c860d32d-ccc0-4cdd-8085-8489221d8745.jpg",
    },
    {
        label: "APIs",
        icon: Code2,
        href: "/apis",
        preview: "https://cdn.pixro.ai/3f9be7ee-28b0-452a-a0eb-f9047204dd88.jpg",
    },
    {
        label: "Contact us",
        icon: Mail,
        href: "/contact",
        preview: "https://cdn.pixro.ai/d0912d97-bdd4-4be1-b4ad-8759f586aa62.jpg",
    },
];

interface ResourcesMenuProps {
    open: boolean;
    onClose: () => void;
}

const ResourcesMenu = ({ open, onClose }: ResourcesMenuProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [hoveredItem, setHoveredItem] = useState<Item | null>(null);
    const { pathname } = useLocation();

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open, onClose]);

    if (!open) return null;

    const activeItem = hoveredItem ?? resources[0];

    return (
        <div
            ref={ref}
            className="fixed left-1/2 top-10 z-50 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-white/10 bg-card/40 backdrop-blur-2xl shadow-2xl pb-[24px] pt-[24px] pl-[24px] pr-[24px] w-auto"
        >
            <div className="flex gap-8 items-stretch">
                <div className="flex-shrink-0">
                    <h4 className="text-xs font-semibold tracking-widest text-muted-foreground mb-[4px] mx-[8px]">
                        RESOURCES
                    </h4>
                    <ul className="flex flex-col gap-1">
                        {resources.map((tool) => {
                            const Icon = tool.icon;
                            return (
                                <li key={tool.label}>
                                    <Link
                                        to={tool.href}
                                        onClick={onClose}
                                        onMouseEnter={() =>
                                            setHoveredItem(tool)
                                        }
                                        className={`flex items-center gap-3 whitespace-nowrap rounded-lg px-2 py-2 text-sm transition-colors hover:bg-muted/40 ${pathname === tool.href || pathname.startsWith(tool.href + "/") ? "text-primary hover:text-primary" : "text-foreground/90 hover:text-foreground"}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                        <span>{tool.label}</span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <div className="flex-shrink-0 flex items-center justify-start">
                    <div className="aspect-square w-[220px] overflow-hidden rounded-2xl">
                        <img
                            key={activeItem.preview}
                            src={activeItem.preview}
                            alt={activeItem.label}
                            loading="lazy"
                            width={768}
                            height={768}
                            className="h-full w-full object-cover"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResourcesMenu;
